import { ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree } from '@angular/router';

import { first, filter, map, catchError, tap, switchMap } from 'rxjs/operators';
import { Observable, of, race } from 'rxjs';

import { BeachyAttributes } from '../../../objects/beachy-object';


export abstract class BeachyGuardBase  {
  protected isDomainObjectValidForGivenParamName<T extends BeachyAttributes>(
    getObjectFromStore: () => Observable<T | null>,
    param: { [key: string]: string | number },
  ): Observable<boolean> {

    const key = Object.keys(param)[0];

    return getObjectFromStore().pipe(
      first(),
      map(value => param[key] === (value as any)?.[key]),
      catchError(_e => of(false)),
    );
  }

  /**
   * Resolves domain object, without checking object validity
   *
   * @param getObjectFromStore selector to get object|objects
   * @param getError selector to get an error
   */
  protected resolveDomainObjectOrThrowAnError<T extends BeachyAttributes | BeachyAttributes[]>(
    getObjectFromStore: () => Observable<T | null>,
    getError: () => Observable<Error | null>,
  ): Observable<T> {
    return race(
      getObjectFromStore().pipe(filter((obj): obj is T => !!obj), first()),
      getError().pipe(
        filter(error => !!error),
        first(),
        map(e => { throw e; }),
      ),
    );
  }

  /**
   * Resolves domain object from the store if object is valid.
   * Validity checked by comparing given param {[key]: value}
   * If there's no valid object in the store, or there's no object
   * at all, this method will trigger load function and try to
   * load valid object into the store.
   *
   * If it fails at any point it will throw an error.
   *
   * @param getObjectFromStore selector to get object from the store
   * @param getError selector to get an error from the store
   * @param loadObjectFromStore method to trigger load data
   * @param param validity check
   */
  protected resolveValidDomainObjectOrThrow<T extends BeachyAttributes>(
    getObjectFromStore: () => Observable<T | null>,
    getError: () => Observable<Error | null>,
    loadObjectFromStore: () => void,
    param: { [key: string]: string | number },
  ): Observable<T> {
    return this.isDomainObjectValidForGivenParamName(
      () => getObjectFromStore(),
      param,
    )
      .pipe(
        tap(isValid => {
          if (!isValid) {
            loadObjectFromStore();
          }
        }),
        switchMap(isValid => {
          if (isValid) {
            return getObjectFromStore().pipe(filter((obj): obj is T => !!obj), first());
          }

          return this.resolveDomainObjectOrThrowAnError(
            () => getObjectFromStore(),
            () => getError(),
          );
        })
      );
  }

  abstract canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): boolean | UrlTree | Observable<boolean | UrlTree> | Promise<boolean | UrlTree>;
}
